import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { toast } from "sonner";
import { logAxiosError, getAxiosErorrMessage } from "./utils";

interface RequestOptions extends AxiosRequestConfig {
  errorMessage?: string;
  showToast?: boolean;
  throwOnError?: boolean;
}

const logRequestDetails = (
  method: string,
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
) => {
  console.error(`[Axios Request] ${method.toUpperCase()} ${url}`);
  if (data) {
    console.error("[Request Body]:", data);
  }
  if (config?.params) {
    console.error("[Request Params]:", config.params);
  }
  if (config?.headers) {
    console.error("[Request Headers]:", config.headers);
  }
};

const handleAxiosError = (
  error: AxiosError | unknown,
  errorMessage: string,
  showToast: boolean = true,
  throwOnError: boolean = true,
) => {
  logAxiosError(errorMessage, error);

  if (showToast && axios.isAxiosError(error)) {
    const fullErrorMessage = `${errorMessage}: ${getAxiosErorrMessage(error)}`;
    toast.error(fullErrorMessage);
  }

  if (throwOnError) {
    throw error;
  }

  return null;
};

export const axiosGet = async <T = any>(
  url: string,
  options: RequestOptions = {},
): Promise<AxiosResponse<T> | null> => {
  const {
    errorMessage = "Failed to fetch data",
    showToast = true,
    throwOnError = true,
    ...config
  } = options;

  try {
    const response: AxiosResponse<T> = await axios.get(url, config);
    return response;
  } catch (error) {
    console.error("Error in axios GET", error);
    logRequestDetails("GET", url, undefined, config);
    return handleAxiosError(error, errorMessage, showToast, throwOnError);
  }
};

export const axiosPost = async <T = any>(
  url: string,
  data?: any,
  options: RequestOptions = {},
): Promise<AxiosResponse<T> | null> => {
  const {
    errorMessage = "Failed to post data",
    showToast = false,
    throwOnError = true,
    ...config
  } = options;

  try {
    const response: AxiosResponse<T> = await axios.post(url, data, config);
    return response;
  } catch (error) {
    console.error("Error in axios POST", error);
    logRequestDetails("POST", url, data, config);
    return handleAxiosError(
      error,
      errorMessage,
      showToast && !config?.signal?.aborted,
      throwOnError,
    );
  }
};

export const axiosPut = async <T = any>(
  url: string,
  data?: any,
  options: RequestOptions = {},
): Promise<AxiosResponse<T> | null> => {
  const {
    errorMessage = "Failed to update data",
    showToast = true,
    throwOnError = true,
    ...config
  } = options;

  try {
    const response: AxiosResponse<T> = await axios.put(url, data, config);
    return response;
  } catch (error) {
    console.error("Error in axios PUT", error);
    logRequestDetails("PUT", url, data, config);
    return handleAxiosError(
      error,
      errorMessage,
      showToast && !config?.signal?.aborted,
      throwOnError,
    );
  }
};

export const axiosPatch = async <T = any>(
  url: string,
  data?: any,
  options: RequestOptions = {},
): Promise<AxiosResponse<T> | null> => {
  const {
    errorMessage = "Failed to patch data",
    showToast = true,
    throwOnError = true,
    ...config
  } = options;

  try {
    const response: AxiosResponse<T> = await axios.patch(url, data, config);
    return response;
  } catch (error) {
    return handleAxiosError(
      error,
      errorMessage,
      showToast && !config?.signal?.aborted,
      throwOnError,
    );
  }
};

export const axiosDelete = async <T = any>(
  url: string,
  options: RequestOptions = {},
): Promise<AxiosResponse<T> | null> => {
  const {
    errorMessage = "Failed to delete data",
    showToast = true,
    throwOnError = true,
    ...config
  } = options;

  try {
    const response: AxiosResponse<T> = await axios.delete(url, config);
    return response;
  } catch (error) {
    console.error("Error in Axios DELETE", error);
    logRequestDetails("DELETE", url, undefined, config);
    return handleAxiosError(
      error,
      errorMessage,
      showToast && !config?.signal?.aborted,
      throwOnError,
    );
  }
};
